import React from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, Chip, Divider } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const GOLD = '#d4af37';

const poojas = [
  {
    name: 'Abhishekam',
    icon: '🪔',
    description: 'Sacred bathing ritual of the deity with holy water, milk, and sandalwood paste',
  },
  {
    name: 'Malar Nivedhyam',
    icon: '🌺',
    description: 'Offering of sacred flowers adorning the deity with garlands and floral arrangements',
  },
  {
    name: 'Dhaara',
    icon: '🍯',
    description: 'Ceremonial pouring of holy water over the deity in continuous stream',
  },
  {
    name: 'Thri Madhuram Nivedhyam',
    icon: '🍶',
    description: 'Sacred offering of three sweet delicacies — honey, jaggery, and sugar',
  },
  {
    name: 'Pushpanjali',
    icon: '💐',
    description: 'Devotional offering of flowers accompanied by sacred chanting and mantras',
  },
  {
    name: 'Nithya Pooja',
    icon: '🙏',
    description: 'The daily worship ritual performed with traditional tantric observances',
  },
];

export default function DailyPoojas() {
  return (
    <Box sx={{ bgcolor: '#faf9f6', minHeight: '100vh' }}>
      <SEO
        title="Daily Poojas — Kottayil Temple | നിത്യ പൂജ"
        description="Daily morning poojas at Kottayil Temple, Muchukunnu Sri Kotta-Kovilakam Kshethram. Abhishekam, Malar Nivedhyam, Dhaara, Pushpanjali, Nithya Pooja schedule and timings."
        path="/poojas/daily"
        keywords="daily poojas, Kottayil temple, abhishekam, malar nivedhyam, dhaara, pushpanjali, nithya pooja, Muchukunnu temple, നിത്യ പൂജ, കൊട്ടയിൽ ക്ഷേത്രം പൂജ, അഭിഷേകം, പുഷ്പാഞ്ജലി, daily pooja timings, morning pooja schedule Kerala"
      />
      <PageBanner title="Daily Poojas" />

      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ── HEADER CARD ── */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              background: 'linear-gradient(135deg, #451a03 0%, #78350f 35%, #92400e 70%, #b45309 100%)',
              position: 'relative',
            }}>
              {/* decorative dots */}
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
              {/* glow */}
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)` }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4.5 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                {/* badge */}
                <Chip
                  icon={<WbSunnyIcon sx={{ fontSize: 14, color: `${GOLD} !important` }} />}
                  label="Morning Worship"
                  size="small"
                  sx={{
                    bgcolor: `${GOLD}18`, color: GOLD, fontWeight: 700, fontSize: '0.72rem',
                    height: 26, mb: 2, border: `1px solid ${GOLD}30`, backdropFilter: 'blur(8px)',
                  }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: '18px',
                    bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
                    border: `2px solid ${GOLD}50`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <TempleHinduIcon sx={{ fontSize: 28, color: GOLD }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
                      Kottayil Temple
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500, mt: 0.3 }}>
                      Daily Morning Poojas
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, maxWidth: 560, mt: 2 }}>
                  The sacred morning rituals at Muchukunnu Sri Kotta-Kovilakam Kshethram, performed daily with traditional tantric observances to invoke divine blessings.
                </Typography>

                {/* quick stats */}
                <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                  <Box sx={{
                    px: 2.5, py: 1.2, borderRadius: '14px',
                    bgcolor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)',
                    border: `1px solid ${GOLD}25`,
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 16, color: GOLD }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
                      6 Sacred Rituals
                    </Typography>
                  </Box>
                  <Box sx={{
                    px: 2.5, py: 1.2, borderRadius: '14px',
                    bgcolor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)',
                    border: `1px solid ${GOLD}25`,
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}>
                    <LocalFloristIcon sx={{ fontSize: 16, color: GOLD }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
                      Performed Daily
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* ── POOJA LIST ── */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '28px',
              border: '1px solid rgba(0,0,0,0.05)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}>
              {/* section header */}
              <Box sx={{
                px: { xs: 2.5, sm: 3.5 }, py: 2.5,
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '12px',
                  background: `linear-gradient(135deg, #92400e, ${GOLD})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(212,175,55,0.25)',
                }}>
                  <WbSunnyIcon sx={{ fontSize: 18, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b' }}>
                    Morning Poojas
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>
                    {poojas.length} rituals performed every morning
                  </Typography>
                </Box>
              </Box>

              {/* pooja items */}
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                {poojas.map((pooja, idx) => (
                  <React.Fragment key={idx}>
                    <Box sx={{
                      display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2.5 },
                      px: { xs: 1.5, sm: 2.5 }, py: { xs: 2, sm: 2.5 },
                      borderRadius: '18px',
                      transition: 'all 300ms cubic-bezier(0.4,0,0.2,1)',
                      '&:hover': {
                        bgcolor: `${GOLD}08`,
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                      },
                    }}>
                      {/* number badge */}
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '15px',
                        background: `linear-gradient(145deg, #92400e, ${GOLD})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, mt: 0.2,
                        boxShadow: '0 4px 16px rgba(212,175,55,0.25)',
                      }}>
                        <Typography sx={{ fontSize: '1.1rem' }}>
                          {pooja.icon}
                        </Typography>
                      </Box>

                      {/* text */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                          <Typography sx={{
                            fontWeight: 800, fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#1e293b',
                            letterSpacing: '-0.01em',
                          }}>
                            {pooja.name}
                          </Typography>
                          <Chip
                            label={`#${idx + 1}`}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.6rem', fontWeight: 800,
                              bgcolor: `${GOLD}12`, color: '#92400e',
                              border: `1px solid ${GOLD}25`,
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.7 }}>
                          {pooja.description}
                        </Typography>
                      </Box>
                    </Box>
                    {idx < poojas.length - 1 && (
                      <Divider sx={{ mx: 2, borderColor: 'rgba(0,0,0,0.04)' }} />
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>

          </Grid>

          {/* ── Sidebar ── */}
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="poojas" activePath="/poojas/daily" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
